import { Box, FormHelperText, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { unstable_usePrompt } from "react-router-dom";
import { CustomEvent } from "schemas/functions";

import Editor from "components/Editor";
import { useMergeTags } from "components/Editor/MergeTag/useMergeTags";
import { extensions } from "components/Editor/utils";
import QuestionChip from "components/Styled/QuestionChip";
import StyledTextField from "components/Styled/TextField";
import { MERGE_TAGS } from "constants/templates";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import useTemplate from "utils/useTemplate";

const styles = {
  errorContent: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": {
        borderColor: "red",
      },
      "& fieldset": {
        borderColor: "red",
      },
    },
  },
  errorText: { color: "red" },
};

interface TemplateBodyProps {
  editTemplate: Template;
  setEditTemplate: Dispatch<SetStateAction<Template>>;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  isNew?: boolean;
  allowChangeType?: boolean;
  allowChangeTitle?: boolean;
  displayError?: boolean;
  isDirty?: boolean;
  minimalist?: boolean;
  displaySignature?: boolean;
  fromAdmin?: boolean;
}

export default function TemplateBody({
  editTemplate,
  setEditTemplate,
  setIsDirty,
  isNew = false,
  allowChangeType = false,
  displayError,
  isDirty,
  allowChangeTitle = true,
  minimalist = false,
  displaySignature = false,
  fromAdmin = false,
}: TemplateBodyProps) {
  const { templates } = useContext(OutreachTemplatesContext);
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const { getUnfilledVariables } = useTemplate();

  const [toggleMergeTagTooltip, toggleVariableTooltip] = useMergeTags();

  const [deletedMergeTag, setDeletedMergeTag] = useState("");
  const [unfilledVariables, setUnfilledVariables] = useState<string[]>([]);

  const displayMergeTagWarning = (deletedMergeTag: string) => {
    setDeletedMergeTag(deletedMergeTag);
  };

  const subjectEditor = useEditor(
    {
      extensions: extensions({
        isPlain: true,
        suggestionOptions: MERGE_TAGS,
        placeholderText: "Email Subject",
      }),
      content: editTemplate?.subject,
      onUpdate({ editor }) {
        console.log("this is so odd");
        setEditTemplate((prevTemplate: Template) => {
          return { ...prevTemplate, subject: editor.getHTML() };
        });

        if (setIsDirty) setIsDirty(true);
      },
    },
    [],
  ) as TipTapEditor;

  const editor = useEditor(
    {
      extensions: extensions({
        toggleVariableTooltip,
        toggleMergeTagTooltip,
        suggestionOptions: MERGE_TAGS,
        displayMergeTagWarning,
        placeholderText: "Email Body",
      }),
      content: editTemplate?.body,
      onUpdate({ editor }) {
        setEditTemplate((prevTemplate: Template) => {
          return { ...prevTemplate, body: editor.getHTML() };
        });
        if (setIsDirty) setIsDirty(true);
      },
    },
    // There is an issue here for all theses editors between new and loading template
    [isNew === true || !!editTemplate.body || editTemplate?.contentType],
  ) as TipTapEditor;

  const signatureEditor = useEditor(
    {
      extensions: extensions({
        placeholderText: "Best, {Your Name}",
        hardBreaksOnly: true,
      }),
      content: editTemplate?.signature,
      onUpdate({ editor }) {
        if (setIsDirty) setIsDirty(true);
        setEditTemplate((prevTemplate: Template) => {
          return { ...prevTemplate, signature: editor.getHTML() };
        });
      },
    },
    [],
  ) as TipTapEditor;

  const handleTemplateChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    setEditTemplate((prevTemplate: Template) => {
      return { ...prevTemplate, [name]: value };
    });

    if (setIsDirty) setIsDirty(true);
  };

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(editTemplate?.body || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.contentType]);

  useEffect(() => {
    const errors = getUnfilledVariables(editTemplate?.body || "");
    setUnfilledVariables(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.body]);

  useEffect(() => {
    if (subjectEditor && editTemplate?.id && editTemplate?.subject) {
      subjectEditor.commands.setContent(editTemplate?.subject || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.id]);

  const hasFirstNameTag = editor && editor?.getHTML()?.includes("First Name");

  const hasBrandNameTag = editor && editor?.getHTML()?.includes("Brand Name");

  const hasRedBorder = !editTemplate?.name && displayError;

  unstable_usePrompt({
    message:
      "You have an unsaved template. Are you sure you want to navigate away from this page?",
    when: ({ currentLocation, nextLocation }) =>
      Boolean(isDirty) && currentLocation.pathname !== nextLocation.pathname,
  });

  const renderVariable = (label: string) => {
    return (
      <Box
        component="span"
        sx={{
          p: 0.5,
          mx: 0.5,
          backgroundColor: "#fce6df",
          color: "black",
          borderRadius: 2,
        }}
        key={label}
      >
        {label}
      </Box>
    );
  };

  return (
    <>
      <Grid container gap={1}>
        {allowChangeTitle && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            {hasRedBorder && (
              <Typography variant="body2" sx={styles.errorText}>
                Please fill in the template name
              </Typography>
            )}
            <StyledTextField
              required
              value={editTemplate?.name}
              onChange={handleTemplateChange}
              fullWidth
              label="Template Name"
              placeholder="Template Name"
              name="name"
              styles={hasRedBorder ? styles.errorContent : {}}
            />
          </Grid>
        )}
        {((allowChangeType && templates?.length > 0) || fromAdmin) && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Typography gutterBottom>Template Type *</Typography>

            <Typography variant="caption" paragraph color="textSecondary">
              Select one option below.
            </Typography>

            <Grid container gap={1}>
              <QuestionChip
                onClick={() => {
                  setEditTemplate((prevTemplate: Template) => {
                    return {
                      ...prevTemplate,
                      contentType: TemplateContentType.OUTREACH,
                    };
                  });

                  if (setIsDirty) setIsDirty(true);
                }}
                isSelected={
                  editTemplate?.contentType === TemplateContentType.OUTREACH
                }
                label="Outreach"
              />
              <QuestionChip
                onClick={() => {
                  setEditTemplate((prevTemplate: Template) => {
                    return {
                      ...prevTemplate,
                      contentType: TemplateContentType.FOLLOWUP,
                    };
                  });

                  if (setIsDirty) setIsDirty(true);
                }}
                isSelected={
                  editTemplate?.contentType === TemplateContentType.FOLLOWUP
                }
                label="Followup"
              />
            </Grid>
          </Grid>
        )}

        {editTemplate?.contentType !== TemplateContentType.FOLLOWUP && (
          <Grid item xs={12}>
            <Typography component={"div"} gutterBottom>
              Subject *{" "}
              {!minimalist && (
                <Box
                  component="span"
                  sx={{ color: "purple.dark", fontSize: 12 }}
                >{`Utilize personalization tags {Brand Name} and {First Name}`}</Box>
              )}
            </Typography>
            <Editor editor={subjectEditor} hideMenu mergeTags={MERGE_TAGS} />
          </Grid>
        )}

        <Editor
          editor={editor}
          mergeTags={!minimalist ? MERGE_TAGS : []}
          allowImage={true}
          allowAttachment={true}
          openAttachmentManager={() => {
            setAttachmentDialogOpen(true);
          }}
          attachment={editTemplate?.attachment}
          removeAttachment={() => {
            setEditTemplate((prevTemplate: Template) => {
              return { ...prevTemplate, attachment: undefined };
            });
            if (setIsDirty) setIsDirty(true);
          }}
        />

        {displaySignature && (
          <>
            Signature: <Editor editor={signatureEditor} allowImage={true} />
          </>
        )}

        <FormHelperText>
          {unfilledVariables?.length > 0 && (
            <>
              Please fill out {unfilledVariables?.map((x) => renderVariable(x))}
            </>
          )}
          {deletedMergeTag === "Brand Name" && !hasBrandNameTag && (
            <>
              Keep{" "}
              <Box
                component="span"
                sx={{
                  backgroundColor: grey[100],
                  p: 0.5,
                  borderRadius: 2,
                  mx: 0.5,
                }}
              >
                Brand Name
              </Box>{" "}
              tag so Bento can customize each outreach emails to the Brand's
              name for you.
            </>
          )}
          {deletedMergeTag === "First Name" && !hasFirstNameTag && (
            <>
              Keep{" "}
              <Box
                component="span"
                sx={{
                  backgroundColor: grey[100],
                  p: 0.5,
                  borderRadius: 2,
                  mx: 0.5,
                }}
              >
                First Name{" "}
              </Box>{" "}
              tag so Bento can customize each outreach emails to the recipient's
              first name for you.
            </>
          )}
        </FormHelperText>
      </Grid>
      <AttachmentManager
        enteredSelection={(attachment: string) => {
          setEditTemplate((prevTemplate: Template) => {
            return { ...prevTemplate, attachment };
          });
          if (setIsDirty) setIsDirty(true);
        }}
        open={attachmentDialogOpen}
        setOpen={setAttachmentDialogOpen}
      />
    </>
  );
}
